// Shared presentational helpers for the long-form legal pages (/privacy,
// /terms). Keeps headings, body copy, lists, and tables consistent across both
// documents and horizontally scroll-safe on narrow screens.

export function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mt-14 scroll-mt-24 font-sans text-2xl font-semibold tracking-tight text-ink">
      {children}
    </h2>
  )
}

export function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-8 font-sans text-lg font-semibold text-ink">{children}</h3>
  )
}

export function P({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-4 text-[17px] leading-relaxed text-ink/75">{children}</p>
  )
}

/** Emphasised inline lead-in for a clause (e.g. "Services, location data."). */
export function Lead({ children }: { children: React.ReactNode }) {
  return (
    <strong className="font-semibold text-ink/85">{children}</strong>
  )
}

export function List({
  items,
  variant = "disc",
}: {
  items: React.ReactNode[]
  variant?: "disc" | "alpha"
}) {
  const cls =
    variant === "alpha"
      ? "mt-4 list-[lower-alpha] space-y-2 pl-6"
      : "mt-4 list-disc space-y-2 pl-6"
  return (
    <ol className={cls}>
      {items.map((item, i) => (
        <li key={i} className="pl-1 text-[17px] leading-relaxed text-ink/75">
          {item}
        </li>
      ))}
    </ol>
  )
}

export function Table({
  head,
  rows,
}: {
  head: string[]
  rows: React.ReactNode[][]
}) {
  return (
    <div className="mt-6 overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-left align-top text-[14px]">
        <thead>
          <tr className="border-b-2 border-line-cool">
            {head.map((h) => (
              <th
                key={h}
                className="py-2 pr-5 align-top font-sans font-semibold text-ink"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-line align-top">
              {row.map((cell, j) => (
                <td key={j} className="py-3 pr-5 align-top text-ink/70">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
